import { useParams } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Shared";
import { HairHero, HairBreadcrumb, HairBand, HairEyebrow, Bullets, Paras, HairRiskPanel, HairFaq, HairCTABand } from "@/components/HairShared";
import { StickyCTA } from "@/components/Landing";
import { OFFERS } from "@/data/landing";
import { HAIR_BREADCRUMBS, HAIR_HERO, TRANSPLANT_HUB, getTransplantSubPage } from "@/data/hair";
import NotFound from "@/pages/NotFound";

const Block = ({ block, dark }) => {
  const text = dark ? "text-warm-white/85" : "text-ink/85";
  const head = dark ? "text-warm-white" : "text-ink";
  if (block.type === "paras") {
    return (
      <Reveal>
        <HairEyebrow className="mb-4">{block.heading}</HairEyebrow>
        <Paras items={block.paras} dark={dark} />
      </Reveal>
    );
  }
  if (block.type === "list") {
    return (
      <Reveal>
        <HairEyebrow className="mb-4">{block.heading}</HairEyebrow>
        <div className="mt-6"><Bullets items={block.items} dark={dark} /></div>
      </Reveal>
    );
  }
  if (block.type === "timeline") {
    return (
      <Reveal>
        <div className="space-y-10">
          {block.items.map((item, i) => (
            <div key={i} className={`flex gap-6 border-b pb-10 last:border-0 last:pb-0 ${dark ? "border-gold/20" : "border-hairline/40"}`} data-testid={`recovery-step-${i}`}>
              <span className="font-serif font-normal text-gold text-lg shrink-0 w-32 sm:w-40">{item.when}</span>
              <p className={`${text} text-[15px] leading-relaxed`}>{item.what}</p>
            </div>
          ))}
        </div>
      </Reveal>
    );
  }
  if (block.type === "callout") {
    return (
      <Reveal>
        <div className={`relative p-8 sm:p-10 ${dark ? "bg-hair-panel" : "bg-cream"}`} data-testid="warning-signs-block">
          <div className="gold-frame inset-3 opacity-80" aria-hidden="true" />
          <div className="relative">
            <h3 className={head}>{block.heading}</h3>
            <p className={`${text} leading-[1.8] mt-4 text-[15px]`}>{block.body}</p>
          </div>
        </div>
      </Reveal>
    );
  }
  if (block.type === "areas") {
    return (
      <Reveal>
        <div className="space-y-12">
          {block.items.map((item, i) => (
            <div key={i} data-testid={`area-${i}`} className={`grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-8 border-b pb-12 last:border-0 last:pb-0 ${dark ? "border-gold/20" : "border-hairline/40"}`}>
              <h3 className={`${head} sm:col-span-4`}>{item.title}</h3>
              <p className={`${text} text-[15px] leading-[1.8] sm:col-span-8`}>{item.body}</p>
            </div>
          ))}
        </div>
      </Reveal>
    );
  }
  return null;
};

const TransplantSub = () => {
  const { subSlug } = useParams();
  const page = getTransplantSubPage(subSlug);
  if (!page) return <NotFound />;

  const crumbs = HAIR_BREADCRUMBS.transplant(page.title, `/hair/transplantation/${page.slug}`);
  const faqSchema = page.faqs?.length
    ? { "@type": "FAQPage", mainEntity: page.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }
    : null;
  const blocksCount = page.blocks.length;

  return (
    <>
      <SEO
        title={page.seoTitle?.replace("Lumière Aesthetics", "Lumière Hair Clinic") || `${page.title} | Lumière Hair Clinic`}
        description={`${page.standfirst} Hair transplantation at Lumière Hair Clinic, Cottesloe — planned and performed by Dr Kristy Truong.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "BreadcrumbList", itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: `https://www.lumiereaesthetics.com.au${c.to}` })) },
            ...(faqSchema ? [faqSchema] : []),
          ],
        }}
      />
      <HairHero eyebrow={page.eyebrow} title={page.title} standfirst={page.standfirst} offer={OFFERS.advanced} image={HAIR_HERO.roomDetail} tall={false} testId={`transplant-sub-hero-${page.slug}`} />
      <HairBreadcrumb items={crumbs} />

      {page.blocks.map((block, i) => (
        <HairBand key={i} dark={i % 2 === 1} narrow testId={`sub-${page.slug}-block-${i}`}>
          <Block block={block} dark={i % 2 === 1} />
        </HairBand>
      ))}

      {page.risks && (
        <HairBand dark={blocksCount % 2 === 1} narrow testId={`sub-${page.slug}-risks`}>
          <Reveal><HairRiskPanel risks={TRANSPLANT_HUB.risks} dark={blocksCount % 2 === 1} /></Reveal>
        </HairBand>
      )}

      {page.faqs?.length > 0 && (
        <HairBand dark narrow testId={`sub-${page.slug}-faq`}>
          <Reveal>
            <HairEyebrow className="mb-4">Questions</HairEyebrow>
            <HairFaq faqs={page.faqs} dark testId="sub-faq" />
          </Reveal>
        </HairBand>
      )}

      <StickyCTA practitioner="Dr Kristy Truong" hair />
      <HairCTABand />
    </>
  );
};

export default TransplantSub;
